import { InstallationRefusal } from '@core/helpers/installation-refusal.helper';

// Where an editor shows a refusal: under the instant, over the candidate list, on the candidate
// that was sent, beside the button that sent it, on the form field, or across the whole dialog.
export type RefusalPlace = 'instant' | 'candidates' | 'candidate' | 'action' | 'form' | 'dialog';

export interface PlacedRefusal {
  place: RefusalPlace;
  messageKey: string;
  // What the dialog was opened on has changed underneath it, so the page must read it again.
  stale: boolean;
}

const BAD_REQUESTS: Record<string, [RefusalPlace, string]> = {
  effective_instant_before_current_period: ['instant', 'effectiveInstantBeforeCurrentPeriod'],
  effective_instant_not_after_current_period: ['instant', 'effectiveInstantNotAfterCurrentPeriod'],
  effective_instant_inside_closed_period: ['instant', 'effectiveInstantInsideClosedPeriod'],
  unknown_channel: ['candidate', 'unknownChannel'],
  channel_kind_mismatch: ['candidate', 'channelKindMismatch'],
  channel_is_net: ['candidate', 'channelIsNet'],
  channel_not_net: ['candidate', 'channelNotNet'],
  off_catalogue_not_accepted: ['candidate', 'offCatalogueNotAccepted'],
  nature_source_kind_mismatch: ['candidate', 'natureSourceKindMismatch'],
  source_kind_not_available: ['dialog', 'sourceKindNotAvailable'],
  point_has_no_binding: ['action', 'pointHasNoBinding'],
  binding_already_retracted: ['action', 'bindingAlreadyRetracted'],
  unknown_role: ['form', 'unknownRole'],
  role_not_selectable: ['form', 'roleNotSelectable'],
  role_deprecated: ['form', 'roleDeprecated'],
  duplicate_role: ['form', 'duplicateRole'],
  single_role_already_has_point: ['form', 'singleRoleAlreadyHasPoint'],
};

const KEY_PREFIX = 'installations.editor.errors.';

function placed(place: RefusalPlace, key: string, stale = false): PlacedRefusal {
  return { place, messageKey: KEY_PREFIX + key, stale };
}

// A 422 or an unknown reason means the request itself was wrong, which the user cannot fix, so it
// reads like any other failure to save.
export function placeRefusal(refusal: InstallationRefusal): PlacedRefusal {
  const { status, reason } = refusal;
  if (status === 400 && reason !== null && reason in BAD_REQUESTS) {
    const [place, key] = BAD_REQUESTS[reason];
    return placed(place, key);
  }
  if (status === 409 && reason === 'channel_already_bound') {
    return placed('candidates', 'channelAlreadyBound');
  }
  if (status === 409 && reason === 'shared_device_timezone_conflict') {
    return placed('candidate', 'timezoneConflict');
  }
  if (status === 409 && reason === 'installation_not_active') {
    return placed('dialog', 'installationNotActive', true);
  }
  if (status === 401 || status === 403) {
    return placed('dialog', 'notAllowed');
  }
  if (status === 404) {
    return placed('dialog', 'gone', true);
  }
  return placed('dialog', 'notSaved');
}
